import { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";
import FeatureComparisonModal from "../../private/financial-insights/utils/guides/modals/FeatureComparisonModal";
import CheckoutFormBody from "./utils/CheckoutFormBody";
import CheckIntent from "./utils/CheckIntent";
import { stripeThemeOptions } from "./utils/stripeThemeOptions";
import { useCheckout } from "./utils/hooks/useCheckout";
import { CHECKOUT_CONFIGS } from "./utils/configs/checkoutConfigs";
import { PAYMENT_STRATEGIES } from "./utils/strategies/paymentStrategies";
import { buildReturnUrl } from "./utils/buildReturnUrl";
import { CHECKOUT_STAGE } from "./utils/stages/checkoutStages";
import SupportPopUp from "../../../components/ui/popup/SupportPopUp";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function StandardCheckout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.auth.user);
  const ista = currentUser?.ista || false;
  const displayEmail = currentUser?.email;

  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  const {
    breadcrumbs,
    service,
    billingType,
    purchaseId,
    isUpgradeDowngradePlan,
  } = location.state || {};

  const redirectStatus = searchParams.get("redirect_status");
  const urlService = searchParams.get("service");
  const urlPurchaseId = searchParams.get("purchaseId");

  const activeService = service || urlService;
  const activePurchaseId = purchaseId || urlPurchaseId;
  const activeBillingType = billingType || (urlService ? "RECURRING" : null);

  const hasValidStateSource = !!(location.state && (service || purchaseId));
  const hasValidUrlRedirectSource = !!(
    redirectStatus &&
    urlService &&
    urlPurchaseId
  );
  const isValidCheckoutAccess =
    hasValidStateSource || hasValidUrlRedirectSource;

  useEffect(() => {
    if (!isValidCheckoutAccess) {
      navigate("/", { replace: true });
    }
  }, [isValidCheckoutAccess, navigate]);

  const { stage, clientSecret, summaryData } = useCheckout({
    service: activeService,
    billingType: activeBillingType,
    purchaseId: activePurchaseId,
    redirectStatus,
    isUpgradeDowngradePlan,
    navigate,
  });

  const handleLaunchSupportClick = (e) => {
    e.preventDefault();
    setIsSupportModalOpen(true);
  };

  if (!isValidCheckoutAccess) {
    return (
      <div className="checkout-container">
        <div style={{ padding: "16px 0" }}>
          <LoadingPlaceholder rows={1} type="card-large" />
        </div>
      </div>
    );
  }

  const checkoutConfig = CHECKOUT_CONFIGS[activeService] || {};
  const SummaryCardComponent = checkoutConfig.summaryComponent;
  const activeBreadcrumbs = breadcrumbs || checkoutConfig.breadcrumbs || [];
  const paymentStrategy = PAYMENT_STRATEGIES[activeBillingType];

  const returnUrl = buildReturnUrl({
    service: activeService,
    purchaseId: activePurchaseId,
  });

  if (stage === CHECKOUT_STAGE.VERIFYING) {
    return (
      <CheckIntent
        redirectStatus={redirectStatus}
        service={urlService}
        purchaseId={urlPurchaseId}
        searchParams={searchParams}
        isUpgradeDowngradePlan={isUpgradeDowngradePlan}
      />
    );
  }

  if (stage === CHECKOUT_STAGE.LOADING) {
    return (
      <div className="checkout-container">
        <Breadcrumb items={activeBreadcrumbs} />
        <div style={{ padding: "16px 0" }}>
          <LoadingPlaceholder rows={1} type="card-large" />
        </div>
      </div>
    );
  }

  if (stage === CHECKOUT_STAGE.ERROR) {
    return (
      <Fragment>
        <div className="checkout-container">
          <Breadcrumb items={activeBreadcrumbs} />
          <div
            className="checkout-body"
            style={{ justifyContent: "center", paddingTop: "40px" }}
          >
            <div
              className="standard-card-container"
              style={{ width: "100%", maxWidth: "600px", padding: "24px" }}
            >
              <h3
                style={{
                  marginTop: 0,
                  color: "#C70000",
                  fontWeight: "400",
                  lineHeight: "140%",
                }}
              >
                Something went wrong
              </h3>
              <p style={{ fontSize: "16px", color: "#40464b" }}>
                Your payment didn’t go through. Please try again in a moment, or{" "}
                <Link
                  to="#"
                  onClick={handleLaunchSupportClick}
                  style={{
                    fontSize: "16px",
                    color: "#006CD1",
                    fontWeight: "300",
                  }}
                >
                  Contact Support
                </Link>{" "}
                if you need help.
              </p>
            </div>
          </div>
        </div>
        {isSupportModalOpen && (
          <SupportPopUp setIsModalOpen={setIsSupportModalOpen} />
        )}
      </Fragment>
    );
  }

  if (stage === CHECKOUT_STAGE.PROCESSING_EXISTING_INTENT) {
    return (
      <Fragment>
        <div className="checkout-container">
          <Breadcrumb items={activeBreadcrumbs} />
          <div
            className="checkout-body"
            style={{ justifyContent: "center", paddingTop: "40px" }}
          >
            <div
              className="standard-card-container"
              style={{ width: "100%", maxWidth: "600px", padding: "24px" }}
            >
              <h3
                style={{
                  marginTop: 0,
                  color: "#006CD1",
                  fontWeight: "400",
                  lineHeight: "140%",
                }}
              >
                Payment Processing
              </h3>
              <p style={{ fontSize: "16px", color: "#40464b" }}>
                We’re confirming your payment now. You’ll be notified once it’s
                complete. Need help?{" "}
                <Link
                  to="#"
                  onClick={handleLaunchSupportClick}
                  style={{
                    fontSize: "16px",
                    color: "#006CD1",
                    fontWeight: "300",
                  }}
                >
                  Contact Support
                </Link>{" "}
                anytime
              </p>
            </div>
          </div>
        </div>
        {isSupportModalOpen && (
          <SupportPopUp setIsModalOpen={setIsSupportModalOpen} />
        )}
      </Fragment>
    );
  }

  if (stage === CHECKOUT_STAGE.ALREADY_SUBSCRIBED) {
    return (
      <div className="checkout-container">
        <Breadcrumb items={activeBreadcrumbs} />
        <div
          className="checkout-body"
          style={{ justifyContent: "center", paddingTop: "40px" }}
        >
          <div
            className="standard-card-container"
            style={{ width: "100%", maxWidth: "600px", padding: "24px" }}
          >
            <h3
              style={{
                marginTop: 0,
                color: "#006CD1",
                fontWeight: "400",
                lineHeight: "140%",
              }}
            >
              You already have access
            </h3>
            <p style={{ fontSize: "16px", color: "#40464b" }}>
              Your Financial Insights subscription is already active. You can
              continue exploring your personalized insights.
            </p>
            <Link
              to="/finances/personal-finances/overview"
              className="cta-button primary rounded"
            >
              Connect Institution
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="checkout-container">
        <Breadcrumb items={activeBreadcrumbs} />

        <div className="checkout-body">
          {/* LEFT COLUMN */}
          <div className="standard-card-container checkout-info-container">
            {/* EMAIL */}
            <div className="checkout-pii-info">
              <div className="checkout-info-header">
                <span className="checkout-info-step">1</span>
                <p className="checkout-info-title">Email</p>
              </div>

              <div className="checkout-info-body">
                <div className="form-input-representation">
                  <p>{displayEmail}</p>
                </div>
              </div>
            </div>

            <div className="grey-separator"></div>

            {/* PAYMENT */}
            <div className="checkout-pii-info">
              <div className="checkout-info-header">
                <span className="checkout-info-step">2</span>
                <p className="checkout-info-title">Payment Details</p>
              </div>

              <div
                className="checkout-info-body"
                style={{ position: "relative" }}
              >
                {!clientSecret && !ista && (
                  <div style={{ padding: "12px 0" }}>
                    <LoadingPlaceholder rows={1} type="card-medium" />
                  </div>
                )}

                {(clientSecret || ista) && (
                  <Elements
                    stripe={stripePromise}
                    options={
                      ista
                        ? { mode: "setup", currency: "usd" } // Dummy provider config for test accounts
                        : {
                            clientSecret,
                            appearance: { theme: "stripe" },
                            ...stripeThemeOptions,
                          }
                    }
                  >
                    <CheckoutFormBody
                      confirmIntent={(args) =>
                        paymentStrategy?.confirmIntent({
                          ...args,
                          isUpgradeDowngradePlan,
                        })
                      }
                      returnUrl={returnUrl}
                      isFreePlan={summaryData?.isFreePlan || false}
                      isTestAccount={ista}
                    />
                  </Elements>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="checkout-summary">
            {SummaryCardComponent && (
              <SummaryCardComponent
                netAmount={summaryData?.netAmount}
                totalAmount={summaryData?.totalAmount}
                planName={summaryData?.planName}
                displayLabel={summaryData?.displayLabel}
                onOpenFullList={() => setShowFeaturesModal(true)}
              />
            )}
          </div>
        </div>
      </div>

      {showFeaturesModal && (
        <FeatureComparisonModal
          planName={summaryData?.planName || "Plan"}
          onClose={() => setShowFeaturesModal(false)}
        />
      )}
    </Fragment>
  );
}

export default StandardCheckout;
